import React, {useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, Theme, Typography} from '@mui/material'
import {PGTypography} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import loginPicPng from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import PageLoader from '@progyny/components/PageLoader'
import {numericRegex, PasswordField} from '@progyny/components/PasswordField'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {
  useResetPasswordMutation,
  useValidatePasswordTokenLazyQuery,
} from '@progyny/generated/graphql'
import {useResponsive} from '@progyny/hooks/useResponsive'
import {RouteEnum, useRoutes} from '@progyny/routes'

import {theme} from '@root/theme'

const useStyles = makeStyles()((theme: Theme) => ({
  form: {
    '& > *': {
      marginBottom: theme.spacing(2),
    },
  },
  formContainer: {
    width: 420,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    '& h4': {
      fontWeight: 'normal',
    },
  },
  description: {
    fontFamily: 'DM Sans',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '24px',
    textAlign: 'center',
    display: 'block',
    marginBottom: theme.spacing(2),
  },
  button: {
    margin: `${theme.spacing(4)} 0`,
    display: 'block',
  },
  register: {
    marginBottom: theme.spacing(2.5),
  },
  registerText: {
    fontSize: 14,
    color: 'gray',
    marginTop: 13,
  },
  textFiled: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}))

const ResetPassword = () => {
  const {t} = useTranslation()
  const isDownSM = useResponsive('down', 'sm')
  const {token, uid} = useParams() as {token: string; uid: string}
  const [resetPassword, {loading}] = useResetPasswordMutation()
  const [validatePasswordToken, {data, loading: passwordValidationLoader}] =
    useValidatePasswordTokenLazyQuery()
  const {enqueueSnackbar} = useSnackbar()
  const {classes} = useStyles()
  const {goTo} = useRoutes()

  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(redirectTimeoutRef.current)
  }, [])

  function redirectToErrorPage() {
    goTo(RouteEnum.PASSWORD_TOKEN_ERROR)
  }

  useEffect(() => {
    validatePasswordToken({
      variables: {
        uid,
        token,
      },
    })
    if (data) {
      const hasError = !data.validatePasswordToken!.isValid
      if (hasError) redirectToErrorPage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, token, uid])

  const formModel: FormModelType = {
    password: {
      value: '',
      required: true,
      validator: (password) => {
        if (password.length < 12) {
          return t('profile_security_password_too_short')
        } else if (numericRegex.test(password)) {
          return t('profile_security_password_numeric_only')
        } else return ''
      },
    },
  }

  const {values, errors, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  function handleSubmit() {
    resetPassword({
      variables: {
        newPassword: values.password,
        token,
        uid,
      },
    })
      .then((res) => {
        if (res.data?.resetPassword?.errors) {
          redirectToErrorPage()
        } else {
          enqueueSnackbar({
            message: t('reset_password_password_has_been_changed_successfully'),
            variant: 'success',
          })
          redirectTimeoutRef.current = setTimeout(() => {
            goTo(RouteEnum.LOGIN)
          }, 2000)
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  if (passwordValidationLoader) return <PageLoader position={'center'} />

  return (
    <SidepanelContainer
      hideSidePanel={isDownSM}
      styleProps={{
        backgroundPicSrc: loginPicPng,
        backgroundColor: theme.palette.primary.main,
        logoColor: 'primary',
      }}
      mainComponent={
        <Box
          className={classes.formContainer}
          sx={
            isDownSM
              ? {display: 'flex', flexDirection: 'column', flex: 1}
              : undefined
          }
        >
          <Typography
            variant='h1'
            fontWeight={500}
            className={classes.register}
            textAlign={'center'}
          >
            {t('reset_password_create_new_password')}
          </Typography>
          {isDownSM && (
            <PGTypography className={classes.description}>
              {t('reset_password_your_password_must_meet_requirements')}
            </PGTypography>
          )}
          <form
            onSubmit={handleOnSubmit}
            style={
              isDownSM
                ? {display: 'flex', flexDirection: 'column', flex: 1}
                : undefined
            }
          >
            <Box mt={1}>
              <PasswordField
                showStrength
                label={t('profile_security_new_password')}
                onChange={handleOnChange}
                errors={errors}
                values={{password: values.password}}
                placeholder={t('register_complete_choose_strong_password')}
                className={classes.textFiled}
              />
            </Box>
            <Box
              sx={
                isDownSM
                  ? {
                      mt: 'auto',
                      position: 'sticky',
                      bottom: 0,
                      bgcolor: 'background.default',
                      pt: 2,
                      pb: 4,
                    }
                  : undefined
              }
            >
              <SubmitButton
                type='submit'
                loading={loading}
                disabled={isDisabled}
                className={classes.button}
                fullWidth
              >
                {t('reset_password_set_password')}
              </SubmitButton>
            </Box>
          </form>
        </Box>
      }
    />
  )
}

export default ResetPassword
